// Content gating logic for JumpBean subscription tiers
import { CHILD_LIMITS } from "../constants/index.js";
/**
 * Level that is always free for all users
 */
export const FREE_LEVEL = 1;
/**
 * Check if a user can access a specific level based on their subscription
 *
 * Rules:
 * - Level 1 is always free for all users
 * - Levels 2-5 require an active paid subscription (family or classroom tier)
 *
 * @param subscription - The user's current subscription (null if no subscription)
 * @param levelNumber - The level number to check access for (1-5)
 * @returns true if the user can access the level, false otherwise
 */
export function canAccessLevel(subscription, levelNumber) {
    // Level 1 is always free
    if (levelNumber === FREE_LEVEL) {
        return true;
    }
    // No subscription means free tier only
    if (!subscription) {
        return false;
    }
    // Check subscription status - must be active or trialing
    if (subscription.status !== "active" && subscription.status !== "trialing") {
        return false;
    }
    // Free tier can only access level 1
    if (subscription.tier === "free") {
        return false;
    }
    // Paid tiers (family, classroom) get all levels
    return true;
}
/**
 * Get the child limit for a given subscription tier
 *
 * @param tier - The subscription tier
 * @returns The maximum number of children allowed for this tier
 */
export function getChildLimit(tier) {
    switch (tier) {
        case "family":
            return CHILD_LIMITS.FAMILY;
        case "classroom":
            return CHILD_LIMITS.CLASSROOM;
        case "free":
        default:
            return CHILD_LIMITS.FREE;
    }
}
/**
 * Check if a user can add another child profile based on their subscription
 *
 * @param subscription - The user's current subscription (null means free tier)
 * @param currentChildCount - The number of children the user currently has
 * @returns true if the user can add another child, false otherwise
 */
export function canAddChild(subscription, currentChildCount) {
    // Use subscription's child_limit if available, otherwise calculate from tier
    const limit = subscription?.childLimit ?? CHILD_LIMITS.FREE;
    return currentChildCount < limit;
}
/**
 * Check if a subscription is currently active (allows access to premium features)
 *
 * @param subscription - The user's subscription
 * @returns true if the subscription is active or in trial, false otherwise
 */
export function isSubscriptionActive(subscription) {
    if (!subscription) {
        return false;
    }
    return subscription.status === "active" || subscription.status === "trialing";
}
/**
 * Check if a subscription has premium access (paid tier with active status)
 *
 * @param subscription - The user's subscription
 * @returns true if the user has premium access, false otherwise
 */
export function hasPremiumAccess(subscription) {
    if (!subscription) {
        return false;
    }
    if (!isSubscriptionActive(subscription)) {
        return false;
    }
    return subscription.tier !== "free";
}
/**
 * Get the maximum accessible level for a subscription
 *
 * @param subscription - The user's subscription
 * @returns The highest level number the user can access
 */
export function getMaxAccessibleLevel(subscription) {
    if (hasPremiumAccess(subscription)) {
        return 5; // All levels
    }
    return FREE_LEVEL; // Only level 1
}
/**
 * Get detailed lock status for a level
 *
 * @param subscription - The user's subscription
 * @param levelNumber - The level to check
 * @returns Lock status with reason and upgrade message if locked
 */
export function getLevelLockStatus(subscription, levelNumber) {
    // Level 1 is always accessible
    if (levelNumber === FREE_LEVEL) {
        return { isLocked: false };
    }
    // No subscription
    if (!subscription) {
        return {
            isLocked: true,
            reason: "no_subscription",
            upgradeMessage: "Upgrade to Family plan to access Level 2 and beyond",
        };
    }
    // Subscription exists but not active
    if (subscription.status !== "active" && subscription.status !== "trialing") {
        return {
            isLocked: true,
            reason: "inactive_subscription",
            upgradeMessage: "Renew your subscription to continue accessing all levels",
        };
    }
    // Free tier
    if (subscription.tier === "free") {
        return {
            isLocked: true,
            reason: "free_tier",
            upgradeMessage: "Upgrade to Family plan to access Level 2 and beyond",
        };
    }
    // Paid and active
    return { isLocked: false };
}
/**
 * Check if a user can generate a referral code based on their subscription
 *
 * Requirements:
 * - Must have an active subscription (status = 'active')
 * - Must be on a paid tier (family or classroom)
 *
 * Note: Additional requirements (account age, engagement) are checked server-side
 *
 * @param subscription - The user's current subscription
 * @returns true if the user meets subscription requirements for referrals
 */
export function canGenerateReferralCode(subscription) {
    if (!subscription)
        return false;
    if (subscription.status !== "active")
        return false;
    return subscription.tier === "family" || subscription.tier === "classroom";
}
//# sourceMappingURL=content-gating.js.map